// components/ProfilePage.jsx - 个人页面组件
import React, { useState, useEffect } from 'react';
import './ProfilePage.css';
import Toast from './common/Toast';
import { formatUnits } from 'ethers';
import { AroundUIHelperContract } from '../contracts/AroundUIHelper';
import { AroundMarketContract } from '../contracts/AroundMarket';
import { useEthers } from '../context/EthersContext';
import { formatCurrencyCompact as formatCurrency } from '../utils/numberFormat';
import { formatErrorMessage } from '../utils/errors';

const mapPositionToRow = (id, pos, uiData) => {
  const yesBalance = Number((pos?.yesBalance ?? pos?.[2] ?? 0n));
  const noBalance = Number((pos?.noBalance ?? pos?.[3] ?? 0n));
  const betSide = yesBalance >= noBalance ? 'yes' : 'no';
  const betAmount = Number(formatUnits((pos?.collateralAmount ?? pos?.[1] ?? 0n), 6));
  const potentialReturn = Number(formatUnits((pos?.volume ?? pos?.[5] ?? 0n), 6));
  const status = uiData?.result === 0 ? 'active' : uiData?.result === 1 ? 'won' : uiData?.result === 2 ? 'lost' : 'pending';
  const endTime = uiData?.endTime ? new Date(Number(uiData.endTime) * 1000) : null;
  return {
    id: Number(id),
    marketId: Number(id),
    marketTitle: uiData?.quest || `Market #${Number(id)}`,
    betSide,
    betAmount,
    potentialReturn,
    status,
    marketStatus: status === 'active' ? 'active' : 'resolved',
    resolution: status === 'won' ? betSide : status === 'lost' ? (betSide === 'yes' ? 'no' : 'yes') : null,
    datePlaced: new Date(),
    marketEndDate: endTime || new Date(),
    shares: yesBalance + noBalance,
    profitLoss: 0,
  };
};

const ProfilePage = ({ onExploreMarkets, signerOverride }) => {
  const [userMarkets, setUserMarkets] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const itemsPerPage = 10;
  const [address, setAddress] = useState('');
  const [toast, setToast] = useState(null);
  const [totalLen, setTotalLen] = useState(0);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [redeeming, setRedeeming] = useState({});
  const [nowTs, setNowTs] = useState(Date.now());
  // Redeem flow removed

  // 模拟用户数据
  const userData = {
    address: address || 'Connect wallet',
    joinedDate: '2023-01-15',
    totalVolume: 12500,
    totalMarkets: 24,
    winningMarkets: 16,
    accuracy: 66.7,
    totalProfit: 2450.50,
    favoriteCategory: 'crypto'
  };

  const { signer: ctxSigner, provider: ctxProvider, addresses: ctxAddrs } = useEthers();

  useEffect(() => {
    const init = async () => {
      try {
        const signer = signerOverride || ctxSigner;
        if (!signer) return;
        const addr = await signer.getAddress();
        setAddress(addr);
      } catch (e) { console.error('init signer failed', e); }
    };
    init();
  }, [ctxSigner, signerOverride]);

  // Removed countdown timer since Redeem flow is being rewritten
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const { AroundUIHelper, AroundMarket, chainId: cfgChainId } = ctxAddrs || {};
        const provider = ctxProvider;
        if (!provider || !AroundUIHelper || !AroundMarket || !address) {
          setLoading(false);
          return;
        }
        try {
          const net = await provider.getNetwork();
          const pChain = Number(net?.chainId);
          if (!Number.isFinite(pChain) || !Number.isFinite(Number(cfgChainId)) || pChain !== Number(cfgChainId)) {
            setTotalLen(0);
            setUserMarkets([]);
            setLoading(false);
            return;
          }
        } catch { }
        try {
          const code = await provider.getCode(AroundMarket);
          if (!code || code === '0x') {
            setTotalLen(0);
            setUserMarkets([]);
            setLoading(false);
            return;
          }
        } catch { }
        const ui = new AroundUIHelperContract(AroundUIHelper, provider);
        const am = new AroundMarketContract(AroundMarket, provider);
        let len = 0;
        try {
          const lenBn = await am.getUserParticipateIdsLen(address);
          len = Number(lenBn || 0);
        } catch (e) {
          len = 0;
        }
        setTotalLen(len);
        const index = Math.max(0, currentPage - 1);
        let idGroup = [];
        let posGroup = [];
        try {
          const res = await ui.batchIndexUserParticipateIdsInfo(address, index, itemsPerPage);
          idGroup = res?.idGroup || [];
          posGroup = res?.userPositionGroup || [];
        } catch (e) {
          idGroup = [];
          posGroup = [];
        }
        const mdPromises = idGroup.map((id) => ui.indexMarketData(id).catch(() => null));
        const mdResults = await Promise.all(mdPromises);
        const rows = idGroup.map((id, i) => {
          const pos = posGroup[i];
          const md = mdResults[i] || {};
          const questVal = (md?.thisPoolInfo?.quest ?? md?.thisPoolInfo?.[4] ?? '');
          const uiData = { ...(md?.thisMarketInfo || {}), quest: questVal };
          return mapPositionToRow(id, pos, uiData);
        }).filter(Boolean);
        const seen = new Set();
        const uniqueRows = [];
        for (const r of rows) {
          const k = String(r.id);
          if (seen.has(k)) continue;
          seen.add(k);
          uniqueRows.push(r);
        }
        setUserMarkets(uniqueRows);
      } catch (e) {
        setToast({ type: 'error', title: 'Load failed', message: formatErrorMessage(e, 'Load failed') });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [address, currentPage, reloadNonce]);

  // 计算当前页数据
  const currentItems = userMarkets;
  const totalPages = Math.max(1, Math.ceil(totalLen / itemsPerPage));

  // 格式化日期
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // 统一格式化

  // 处理页码变化
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 渲染分页控件
  const renderPagination = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="pagination">
        <button
          className="page-btn"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>

        {startPage > 1 && (
          <>
            <button
              className={`page-btn ${1 === currentPage ? 'active' : ''}`}
              onClick={() => handlePageChange(1)}
            >
              1
            </button>
            {startPage > 2 && <span className="page-ellipsis">...</span>}
          </>
        )}

        {pageNumbers.map(number => (
          <button
            key={number}
            className={`page-btn ${number === currentPage ? 'active' : ''}`}
            onClick={() => handlePageChange(number)}
          >
            {number}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="page-ellipsis">...</span>}
            <button
              className={`page-btn ${totalPages === currentPage ? 'active' : ''}`}
              onClick={() => handlePageChange(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          className="page-btn"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    );
  };

  const shortAddress = address ? `${address.slice(0, 6)}...${address.slice(-4)}` : (userData.address || 'Connect wallet');
  const handleRedeem = async (marketId) => {
    try {
      const signer = signerOverride || ctxSigner;
      if (!signer) {
        setToast({ type: 'error', title: '需要钱包', message: '请连接钱包后再赎回' });
        return;
      }
      const { AroundMarket } = ctxAddrs || {};
      if (!AroundMarket) {
        setToast({ type: 'error', title: '配置错误', message: '未找到合约地址' });
        return;
      }
      setRedeeming((p) => ({ ...p, [marketId]: true }));
      const am = new AroundMarketContract(AroundMarket, signer);
      const tx = await am.redeemWinnings(marketId);
      setToast({ type: 'info', title: '交易已提交', message: '正在赎回收益', txHash: tx?.hash });
      const receipt = await tx.wait();
      setToast({
        type: 'success',
        title: '赎回成功',
        message: '收益已领取',
        txHash: receipt?.transactionHash || tx?.hash,
      });
      setReloadNonce((n) => n + 1);
    } catch (e) {
      setToast({ type: 'error', title: '赎回失败', message: formatErrorMessage(e, '赎回失败，请稍后重试') });
    } finally {
      setRedeeming((p) => ({ ...p, [marketId]: false }));
    }
  };
  const formatRemain = (ms) => {
    const s = Math.ceil(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(sec)}`;
  };
  return (
    <div className="profile-page">
      <div className="back-nav"><a className="back-link" href="/">← Back to Markets</a></div>
      <div className="profile-header">
        <h1>{shortAddress}</h1>
      </div>

      <div className="profile-content">
        <div className="profile-main">
          <div className="markets-section">
            <div className="section-header">
              <h2>My Markets</h2>
            </div>

            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading your markets...</p>
              </div>
            ) : (
              <>
                <div className="markets-table-container">
                  <table className="markets-table">
                    <colgroup>
                      <col style={{ width: '22%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '10%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="tooltip" data-title="Market">Market</th>
                        <th className="align-center tooltip" data-title="Your Bet">Your Bet</th>
                        <th className="align-right tooltip" data-title="Bet Amount">Bet Amount</th>
                        <th className="align-right tooltip" data-title="Potential Return">Potential Return</th>
                        <th className="align-center tooltip" data-title="Status">Status</th>
                        <th className="align-center tooltip" data-title="Date Placed">Date Placed</th>
                        <th className="align-right tooltip" data-title="P&amp;L">P&L</th>
                        <th className="align-center tooltip" data-title="Action">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.length > 0 ? (
                        currentItems.map(market => (
                          <tr key={market.id} className="market-row">
                            <td className="market-info tooltip" data-title={market.marketTitle}>
                              <span className="market-title tooltip" title={market.marketTitle} data-title={market.marketTitle}>
                                {market.marketTitle}
                              </span>
                              <div className="market-meta">
                                <span className="market-id tooltip" title={`#${market.marketId}`} data-title={`#${market.marketId}`}>#{market.marketId}</span>
                                <span
                                  className="market-end tooltip"
                                  title={`Ends: ${new Date(market.marketEndDate).toLocaleString()}`}
                                  data-title={`Ends: ${new Date(market.marketEndDate).toLocaleString()}`}
                                >
                                  Ends: {formatDate(market.marketEndDate)}
                                </span>
                              </div>
                            </td>
                            <td className="align-center">
                              <div className={`bet-side ${market.betSide} tooltip`} title={market.betSide.toUpperCase()} data-title={market.betSide.toUpperCase()}>
                                {market.betSide.toUpperCase()}
                              </div>
                            </td>
                            <td className="bet-amount align-right tooltip" title={formatCurrency(market.betAmount)} data-title={formatCurrency(market.betAmount)}>
                              {formatCurrency(market.betAmount)}
                            </td>
                            <td className="potential-return align-right tooltip" title={formatCurrency(market.potentialReturn)} data-title={formatCurrency(market.potentialReturn)}>
                              {formatCurrency(market.potentialReturn)}
                            </td>
                            <td>
                              <div className={`status-badge ${market.status} tooltip`} title={market.status} data-title={market.status}>
                                {market.status === 'active' ? 'Active' :
                                  market.status === 'won' ? 'Won' :
                                    market.status === 'lost' ? 'Lost' : 'Pending'}
                              </div>
                            </td>
                            <td
                              className="date-placed align-center tooltip"
                              title={new Date(market.datePlaced).toLocaleString()}
                              data-title={new Date(market.datePlaced).toLocaleString()}
                            >
                              {formatDate(market.datePlaced)}
                            </td>
                            <td className="align-right">
                              <div
                                className={`profit-loss ${market.profitLoss >= 0 ? 'positive' : 'negative'} tooltip`}
                                title={formatCurrency(market.profitLoss)}
                                data-title={formatCurrency(market.profitLoss)}
                              >
                                {market.profitLoss >= 0 ? '+' : ''}{formatCurrency(market.profitLoss)}
                              </div>
                            </td>
                            <td className="align-center">
                              {(() => {
                                const canRedeem = market.status === 'won';
                                if (market.status !== 'won') {
                                  return <span className="tooltip" title="No action" data-title="No action">—</span>;
                                }
                                return (
                                  <button
                                    className="table-action-btn tooltip"
                                    title={canRedeem ? 'Redeem winnings' : 'Redeem unavailable'}
                                    data-title={canRedeem ? 'Redeem winnings' : 'Redeem unavailable'}
                                    onClick={() => handleRedeem(market.id)}
                                    disabled={!canRedeem || !!redeeming[market.id]}
                                  >
                                    {redeeming[market.id] ? 'Redeeming...' : 'Redeem'}
                                  </button>
                                );
                              })()}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="no-markets">
                            <div className="empty-state">
                              <div className="empty-icon">📊</div>
                              <h3>No markets found</h3>
                              <p>You haven't participated in any markets yet.</p>
                              <button className="explore-btn" onClick={onExploreMarkets}>Explore Markets</button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {totalLen > itemsPerPage && (
                  <div className="pagination-section">
                    <div className="pagination-info">
                      Showing {Math.min(totalLen, (currentPage - 1) * itemsPerPage + 1)}-{Math.min(currentPage * itemsPerPage, totalLen)} of {totalLen} markets
                    </div>
                    {renderPagination()}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Toast
        open={!!toast}
        type={toast?.type}
        title={toast?.title}
        message={toast?.message}
        txHash={toast?.txHash}
        chainId={toast?.chainId}
        onClose={() => setToast(null)}
      />
    </div >
  );
};

export default ProfilePage;
